import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { generateLayers } from "../../utils/API";
import { ConfigContext } from "../../utils/ConfigContext";

const useLayerList = () => {
  const { config } = useContext(ConfigContext);
  const activeModule = useSelector((state) => state.activeModule);
  const operationLayers = useSelector(
    (state) => state.mapViewReducer.operationLayers
  );

  const [layers, setLayers] = useState([]);

  useEffect(() => {
    let timer = 0;

    timer = setTimeout(() => {
      const layerMap = new Map();
      const layerInformationMap = new Map();
      const layerTitlesFunc = new Map();

      //getting all layers from config
      const { moduleMap, restLayers } = generateLayers(config);

      const { [activeModule]: currentModuleLayers, ...restModules } = moduleMap;

      const activeModuleLayers = currentModuleLayers?.layers || [];
      const activeModuleOptionalLayers =
        moduleMap[activeModule]?.optionalLayers || [];
      // Combine all arrays in restModules into one array without duplicates
      const restModuleLayers = Array.from(
        new Set(
          Object.values({
            ...restModules,
            configLayers: {
              layers: restLayers,
              optionalLayers: [],
            },
          }).flatMap((module) => [...module.layers, ...module.optionalLayers])
        )
      );

      // Create a priority map
      const priorityMap = new Map();
      activeModuleLayers.forEach((layer, index) => {
        priorityMap.set(layer, index);
      });
      activeModuleOptionalLayers.forEach((layer, index) => {
        if (!priorityMap.get(layer)) {
          priorityMap.set(layer, index + activeModuleLayers.length);
        }
      });
      restModuleLayers.forEach((layer, index) => {
        if (!priorityMap.get(layer)) {
          priorityMap.set(
            layer,
            index +
              activeModuleLayers.length +
              activeModuleOptionalLayers.length
          );
        }
      });

      // Sort operationLayers based on the priority map
      const sortedOperationLayers = [...operationLayers].sort((a, b) => {
        const priorityA = priorityMap.get(a.alias) ?? Infinity;
        const priorityB = priorityMap.get(b.alias) ?? Infinity;
        return priorityA - priorityB;
      });

      sortedOperationLayers.map((layer) => {
        if (layer.layerConfig.alias !== "locations_basemap") {
          const newLayer = layer;
          const title = newLayer.title;
          const information = newLayer.information;

          if (layerMap.has(title)) {
            const layerObj = layerMap.get(title);

            layerObj.push(newLayer);
          } else {
            layerInformationMap.set(title, information);
            layerMap.set(title, [newLayer]);
            layerTitlesFunc.set(title, {
              getLayerTitle: newLayer.getLayerTitle,
              getLayerInformation: newLayer.getLayerInformation,
            });
          }
        }
      });

      // Convert layerMap to array of objects with key as title and value as alias
      const layersArray = Array.from(layerMap.entries()).map(
        ([title, groupedLayers]) => ({
          title,
          information: layerInformationMap.get(title),
          groupedLayers,
          getLayerTitle: layerTitlesFunc.get(title)?.getLayerTitle,
          getLayerInformation: layerTitlesFunc.get(title).getLayerInformation,
        })
      );

      setLayers(layersArray);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [operationLayers, activeModule, config]);

  return [layers, setLayers];
};

export default useLayerList;
